import { useContext, useEffect, useState, lazy, Suspense } from "react";
import "./App.css";
import { LocationContext } from "./contexts/Location";
import { getAllTrail } from "./utils/api";
import { HomePage } from "./components/HomePage";
import { StatisticsPage } from "./components/session/StatisticsPage";
import { BadgesPage } from "./components/BadgesPage";
import { ManagementPage } from "./components/ManagementPage";
import { useAuth } from "./contexts/AuthContext";
import { LoginPage } from "./components/auth/LoginPage";
import { ProfilePage } from "./components/ProfilePage";
import { DashboardDetail } from "./components/dashboard/DashboardDetail";
import MainLayout from "./components/common/MainLayout"; // Import MainLayout
import { TrailForm } from "./components/trail/TrailForm";
import { TrailDetail } from "./components/trail/TrailDetail";
import { EmptyState } from "./components/common/EmptyState";

import {
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  Outlet,
} from "react-router-dom";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { Trail } from "./types/trail";
import { ViewModeProvider } from "./contexts/ViewModeContext";

// Lazy-loaded components
const AdminPage = lazy(() => import("./components/AdminPage"));
const TrainerDashboard = lazy(() => import("./components/TrainerDashboard"));
const DashboardsListPage = lazy(() => import("./components/DashboardsListPage"));

/**
 * @function App
 * @description The root component of the application. Handles initial loading, authentication,
 * and sets up the `LocationProvider` and `ViewModeProvider` for the rest of the app.
 */
function App() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const { trails, setTrails, triggerGetTrails } = useContext(LocationContext);

  useEffect(() => {
    if (user) {
      getAllTrail().then((data) => {
        setTrails(data);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerGetTrails, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl font-semibold">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <ViewModeProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<MainLayout />}>
            {" "}
            {/* Use MainLayout for authenticated routes */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/home"
              element={
                <HomePage
                  trails={trails}
                  onViewTrails={() => navigate("/trails")}
                  onCreateNew={() => navigate("/trails/new")}
                  onViewStatistics={() => navigate("/statistics")}
                  onViewBadges={() => navigate("/badges")}
                />
              }
            />
            <Route
              path="/statistics"
              element={<StatisticsPage trails={trails} />}
            />
            <Route path="/badges" element={<BadgesPage trails={trails} />} />
            <Route path="/management" element={<ManagementPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            {/* Admin Routes */}
            <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
              <Route
                path="/admin"
                element={
                  <Suspense fallback={<div>Loading Admin Page...</div>}>
                    <AdminPage />
                  </Suspense>
                }
              />
            </Route>
            {/* Trainer Routes */}
            <Route element={<ProtectedRoute allowedRoles={["trainer"]} />}>
              <Route
                path="/trainer-dashboard"
                element={
                  <Suspense fallback={<div>Loading Trainer Dashboard...</div>}>
                    <TrainerDashboard />
                  </Suspense>
                }
              />
              <Route
                path="/sessions"
                element={
                  <Suspense fallback={<div>Loading Dashboards List...</div>}>
                    <DashboardsListPage
                      onNavigateToDashboard={(id: string) => navigate(`/sessions/${id}`)}
                    />
                  </Suspense>
                }
              />
              <Route
                path="/sessions/:dashboardId"
                element={
                  <DashboardDetailWrapper
                    onBack={() => navigate("/sessions")}
                  />
                }
              />
            </Route>
            {/* Trail Management Routes */}
            <Route path="/trails" element={<Outlet />}>
              {" "}
              {/* TrailsLayout is now integrated into MainLayout */}
              <Route
                index
                element={
                  <EmptyState onCreateNew={() => navigate("/trails/new")} />
                }
              />
              <Route path="new" element={<TrailFormWrapper />} />
              <Route path=":trailId" element={<TrailDetailWrapper />} />
              <Route path=":trailId/edit" element={<TrailFormWrapper />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/home" replace />} />{" "}
        {/* Catch-all for unknown routes */}
      </Routes>
    </ViewModeProvider>
  );
}

// Helper components for wrapping pages that need URL parameters
const TrailDetailWrapper = () => {
  const { trailId } = useParams();
  const { trails, setTriggerGetTrails } = useContext(LocationContext);
  const navigate = useNavigate();
  const selectedTrail = trails.find((t) => (t.id || t._id) === trailId);

  useEffect(() => {
    if (!selectedTrail) {
      navigate("/trails");
    }
  }, [selectedTrail, navigate]);

  if (!selectedTrail) {
    return <div className="text-center p-4">Piste introuvable.</div>;
  }

  return (
    <TrailDetail
      trail={selectedTrail}
      onEdit={(trail: Trail) =>
        navigate(`/trails/${trail.id || trail._id}/edit`)
      }
      onDeleteSuccess={() => {
        setTriggerGetTrails(true);
        navigate("/trails");
      }}
    />
  );
};

const TrailFormWrapper = () => {
  const { trailId } = useParams();
  const { trails, setTriggerGetTrails } = useContext(LocationContext);
  const navigate = useNavigate();
  const [editingTrail, setEditingTrail] = useState<Trail | undefined>(
    undefined
  );

  useEffect(() => {
    if (trailId) {
      const trailToEdit = trails.find((t) => (t.id || t._id) === trailId);
      setEditingTrail(trailToEdit);
    } else {
      setEditingTrail(undefined);
    }
  }, [trailId, trails]);

  return (
    <TrailForm
      trail={editingTrail}
      onSaveSuccess={() => {
        setTriggerGetTrails(true);
        navigate(trailId ? `/trails/${trailId}` : "/trails");
      }}
      onCancel={() => navigate(trailId ? `/trails/${trailId}` : "/trails")}
    />
  );
};

const DashboardDetailWrapper = ({ onBack }: { onBack: () => void }) => {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  return (
    <DashboardDetail
      dashboardId={dashboardId || ""}
      onBack={onBack}
      onNavigateToSession={(sessionId) =>
        navigate(`/sessions/${dashboardId}/detail/${sessionId}`)
      } // Example for session detail route
    />
  );
};

// TrailsLayout is now integrated into MainLayout, so this component is no longer needed
// const TrailsLayout = () => {
//   const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
//   const { trails } = useContext(LocationContext);
//   const navigate = useNavigate();
//   const { trailId } = useParams(); // Get trailId from params for sidebar selection

//   // Determine selectedTrailId for TrailList from URL
//   const selectedTrailIdFromUrl = trailId || null;

//   return (
//     <div className="flex-1 flex overflow-hidden">
//       <div
//         className={`flex-shrink-0 border-r border-border shadow-lg transition-all duration-300 ${
//           isSidebarCollapsed ? "w-16" : "w-80"
//         }`}
//       >
//         <TrailList
//           trails={trails}
//           selectedTrailId={selectedTrailIdFromUrl}
//           onSelectTrail={(id) => navigate(`/trails/${id}`)}
//           onCreateNew={() => navigate('/trails/new')}
//           isCollapsed={isSidebarCollapsed}
//           onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
//         />
//       </div>
//       <div className="flex-1">
//         <Outlet /> {/* Renders nested routes */}
//       </div>
//     </div>
//   );
// };

export default App;
